import React, { useState, useEffect } from 'react';
import { Upload, Download, Trash2, Image as ImageIcon, ExternalLink, Flame, Check } from 'lucide-react';
import { getLogo, saveLogo, exportStoreData, importStoreData } from '../../data/storeData';

const SettingsTab = () => {
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLogo();
  }, []);

  const loadLogo = async () => {
    try {
      const logoData = await getLogo();
      setLogo(logoData);
      setLogoPreview(logoData);
    } catch (error) {
      console.error('Error loading logo:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveLogo = async () => {
    if (!logoPreview) {
      alert('Please upload a logo first');
      return;
    }

    try {
      await saveLogo(logoPreview);
      setLogo(logoPreview);
      alert('Logo saved successfully!');
    } catch (error) {
      console.error('Error saving logo:', error);
      alert('Error saving logo');
    }
  };

  const handleRemoveLogo = async () => {
    if (!window.confirm('Remove store logo?')) return;

    try {
      await saveLogo(null);
      setLogo(null);
      setLogoPreview(null);
      alert('Logo removed!');
    } catch (error) {
      console.error('Error removing logo:', error);
      alert('Error removing logo');
    }
  };

  const handleExport = async () => {
    try {
      await exportStoreData();
      alert('Data exported successfully! Check your Downloads folder.');
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Error exporting data');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!window.confirm('This will replace ALL current data. Continue?')) {
      e.target.value = '';
      return;
    }

    try {
      await importStoreData(file);
      alert('Data imported successfully! Refreshing page...');
      setTimeout(() => window.location.reload(), 1000);
    } catch (error) {
      console.error('Error importing data:', error);
      alert('Error importing data: ' + error.message);
    }

    e.target.value = '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="w-12 h-12 border-[3px] border-orange-500/20 border-t-orange-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500 font-medium">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Logo Settings */}
      <div className="bg-white rounded-3xl border border-gray-100 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.06)] p-6 md:p-8">
        <div className="mb-7">
          <div className="w-10 h-1 bg-orange-500 rounded-full mb-3" />
          <h2 className="text-2xl font-bold text-[#1d1d1f] tracking-tight">Store Logo</h2>
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
          <div className="w-32 h-32 border-2 border-dashed border-gray-200 rounded-2xl flex items-center justify-center overflow-hidden bg-[#fbfbfd] flex-shrink-0">
            {logoPreview ? (
              <img src={logoPreview} alt="Logo" className="max-w-full max-h-full object-contain p-2" />
            ) : (
              <ImageIcon className="w-10 h-10 text-gray-300" strokeWidth={1.25} />
            )}
          </div>

          <div className="flex-1">
            <p className="text-sm text-gray-500 mb-4 leading-relaxed">
              Upload a logo for your store. Recommended size: 200×60px (PNG with transparent background).
            </p>

            <div className="flex flex-wrap gap-3">
              <label className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1d1d1f] text-white rounded-full hover:bg-black cursor-pointer text-sm font-semibold transition-all active:scale-95 shadow-sm">
                <Upload className="w-4 h-4" strokeWidth={1.75} />
                Upload Logo
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
              </label>

              {logoPreview && logoPreview !== logo && (
                <button
                  onClick={handleSaveLogo}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-orange-500 text-white rounded-full hover:bg-orange-600 text-sm font-semibold transition-all active:scale-95 shadow-[0_8px_20px_-6px_rgba(249,115,22,0.4)]"
                >
                  <Check className="w-4 h-4" strokeWidth={2} />
                  Save Logo
                </button>
              )}

              {logo && (
                <button
                  onClick={handleRemoveLogo}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-200 text-red-500 rounded-full hover:bg-red-50 hover:border-red-200 text-sm font-semibold transition-all active:scale-95"
                >
                  <Trash2 className="w-4 h-4" strokeWidth={1.75} />
                  Remove Logo
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Backup & Restore */}
      <div className="bg-white rounded-3xl border border-gray-100 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.06)] p-6 md:p-8">
        <div className="mb-7">
          <div className="w-10 h-1 bg-orange-500 rounded-full mb-3" />
          <h2 className="text-2xl font-bold text-[#1d1d1f] tracking-tight">Backup &amp; Restore</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Export */}
          <div className="bg-[#fbfbfd] border border-gray-100 rounded-2xl p-6">
            <h3 className="text-lg font-bold text-[#1d1d1f] mb-2 tracking-tight">Export Data</h3>
            <p className="text-sm text-gray-500 mb-5 leading-relaxed">
              Download all your store data (products, categories, logo) as a JSON file for backup or migration.
            </p>
            <button
              onClick={handleExport}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1d1d1f] text-white rounded-full hover:bg-black text-sm font-semibold transition-all active:scale-95 shadow-sm"
            >
              <Download className="w-4 h-4" strokeWidth={1.75} />
              Export Backup
            </button>
          </div>

          {/* Import */}
          <div className="bg-[#fbfbfd] border border-gray-100 rounded-2xl p-6">
            <h3 className="text-lg font-bold text-[#1d1d1f] mb-2 tracking-tight">Import Data</h3>
            <p className="text-sm text-gray-500 mb-5 leading-relaxed">
              Restore data from a backup file. <strong className="text-red-500">Warning:</strong> this replaces ALL current data!
            </p>
            <label className="inline-flex items-center gap-2 px-5 py-2.5 bg-orange-500 text-white rounded-full hover:bg-orange-600 cursor-pointer text-sm font-semibold transition-all active:scale-95 shadow-[0_8px_20px_-6px_rgba(249,115,22,0.4)]">
              <Upload className="w-4 h-4" strokeWidth={1.75} />
              Import Backup
              <input
                type="file"
                accept=".json"
                onChange={handleImport}
                className="hidden"
              />
            </label>
          </div>
        </div>
      </div>

      {/* Firebase Info */}
      <div className="bg-gradient-to-br from-[#1d1d1f] to-black rounded-3xl p-6 md:p-8 text-white relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-[300px] h-[300px] bg-orange-500/10 rounded-full blur-[100px] pointer-events-none" />
        <div className="relative">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-orange-500/15 border border-orange-500/20 rounded-xl flex items-center justify-center">
              <Flame className="w-5 h-5 text-orange-500" strokeWidth={1.75} />
            </div>
            <h3 className="text-lg font-bold tracking-tight">Firebase Storage</h3>
          </div>
          <p className="text-sm text-gray-400 mb-5 leading-relaxed">
            All your data is stored in Firebase Firestore and synced across all devices in real-time.
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-sm text-gray-300">
            {[
              'Data automatically backed up by Firebase',
              'Real-time sync across all users',
              'No localStorage — everything in the cloud',
              'Products visible to all customers instantly'
            ].map((item) => (
              <li key={item} className="flex items-center gap-2.5">
                <Check className="w-4 h-4 text-green-400 flex-shrink-0" strokeWidth={2.5} />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Useful Links */}
      <div className="bg-white rounded-3xl border border-gray-100 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.06)] p-6 md:p-8">
        <div className="mb-7">
          <div className="w-10 h-1 bg-orange-500 rounded-full mb-3" />
          <h2 className="text-2xl font-bold text-[#1d1d1f] tracking-tight">Useful Links</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <a
            href="https://console.firebase.google.com"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center justify-between px-5 py-4 bg-[#fbfbfd] border border-gray-100 hover:border-gray-200 hover:bg-white hover:shadow-[0_12px_28px_-12px_rgba(0,0,0,0.12)] rounded-2xl transition-all duration-300"
          >
            <div>
              <div className="font-semibold text-[#1d1d1f]">Firebase Console</div>
              <div className="text-sm text-gray-400">Manage your Firebase project</div>
            </div>
            <ExternalLink className="w-4 h-4 text-gray-300 group-hover:text-[#1d1d1f] transition-colors" strokeWidth={1.75} />
          </a>

          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center justify-between px-5 py-4 bg-[#fbfbfd] border border-gray-100 hover:border-gray-200 hover:bg-white hover:shadow-[0_12px_28px_-12px_rgba(0,0,0,0.12)] rounded-2xl transition-all duration-300"
          >
            <div>
              <div className="font-semibold text-[#1d1d1f]">View Store</div>
              <div className="text-sm text-gray-400">See your store as customers see it</div>
            </div>
            <ExternalLink className="w-4 h-4 text-gray-300 group-hover:text-[#1d1d1f] transition-colors" strokeWidth={1.75} />
          </a>
        </div>
      </div>
    </div>
  );
};

export default SettingsTab;
